import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AdminShell } from '@/components/layout/AdminShell';
import { Shell } from '@/components/layout/Shell';
import { Skeleton } from '@/components/ui/skeleton';
import { RequireAdmin } from '@/lib/auth/RequireAdmin';
import { NotFound } from '@/routes/NotFound';
import { Recover } from '@/routes/Recover';
import { SignIn } from '@/routes/SignIn';

// Heavy / less-frequently-hit routes are code-split so the focused-home + Shell
// bundle stays small. Leaflet, Recharts, the admin app, and the dense
// SystemOverview view are the largest payloads — each is loaded only when
// its route is visited.
const LiveMap = React.lazy(() => import('@/routes/LiveMap').then((m) => ({ default: m.LiveMap })));
const StopDepartures = React.lazy(() =>
  import('@/routes/StopDepartures').then((m) => ({ default: m.StopDepartures })),
);
const RouteDashboard = React.lazy(() =>
  import('@/routes/RouteDashboard').then((m) => ({ default: m.RouteDashboard })),
);
const TripTimeline = React.lazy(() =>
  import('@/routes/TripTimeline').then((m) => ({ default: m.TripTimeline })),
);
const CompareLab = React.lazy(() =>
  import('@/routes/CompareLab').then((m) => ({ default: m.CompareLab })),
);
const AlertsCenter = React.lazy(() =>
  import('@/routes/AlertsCenter').then((m) => ({ default: m.AlertsCenter })),
);
// PerformanceExplorer (/explore) is parked for launch — see docs/explore-parked.md.
// The route and nav entry are removed so /explore falls through to NotFound; the
// component + its unit test are kept intact for an easy un-park.
const SystemOverview = React.lazy(() => import('@/routes/Home').then((m) => ({ default: m.Home })));
const DataSummary = React.lazy(() =>
  import('@/routes/DataSummary').then((m) => ({ default: m.DataSummary })),
);
const Today = React.lazy(() => import('@/routes/Today').then((m) => ({ default: m.Today })));
// Dev-only component gallery for visual checks (docs/visual-checks.md).
// `import.meta.env.DEV` is statically false in prod builds, so the route and
// its lazy chunk are tree-shaken out.
const Gallery = React.lazy(() => import('@/visual/Gallery').then((m) => ({ default: m.Gallery })));
const You = React.lazy(() => import('@/routes/You').then((m) => ({ default: m.You })));
const AuthVerify = React.lazy(() =>
  import('@/routes/AuthVerify').then((m) => ({ default: m.AuthVerify })),
);
const AdminDashboard = React.lazy(() =>
  import('@/routes/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const AdminLogin = React.lazy(() =>
  import('@/routes/AdminLogin').then((m) => ({ default: m.AdminLogin })),
);
const AdminSystem = React.lazy(() =>
  import('@/routes/AdminSystem').then((m) => ({ default: m.AdminSystem })),
);
const AdminUsage = React.lazy(() =>
  import('@/routes/AdminUsage').then((m) => ({ default: m.AdminUsage })),
);
const AdminErrors = React.lazy(() =>
  import('@/routes/AdminErrors').then((m) => ({ default: m.AdminErrors })),
);
const AdminErrorDetail = React.lazy(() =>
  import('@/routes/AdminErrorDetail').then((m) => ({ default: m.AdminErrorDetail })),
);
const DesignIndex = React.lazy(() =>
  import('@/routes/admin/DesignIndex').then((m) => ({ default: m.DesignIndex })),
);
const DesignTodayTripBasisOverflowMenu = React.lazy(() =>
  import('@/routes/design/today-trip-basis/overflow-menu').then((m) => ({
    default: m.OverflowMenu,
  })),
);

function RouteFallback(): React.JSX.Element {
  return (
    <div className="space-y-3 p-6" data-testid="route-fallback">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function lazyRoute(node: React.ReactNode): React.JSX.Element {
  return <React.Suspense fallback={<RouteFallback />}>{node}</React.Suspense>;
}

export function App(): React.JSX.Element {
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Navigate to="/today" replace />} />
        <Route path="today" element={lazyRoute(<Today />)} />
        <Route path="you" element={lazyRoute(<You />)} />
        <Route path="map" element={lazyRoute(<LiveMap />)} />
        <Route path="system" element={lazyRoute(<SystemOverview />)} />
        {/* Legacy URL preserved as alias of the dense system view. */}
        <Route path="overview" element={lazyRoute(<SystemOverview />)} />
        <Route path="stops/:stopId" element={lazyRoute(<StopDepartures />)} />
        <Route path="routes/:routeId" element={lazyRoute(<RouteDashboard />)} />
        <Route path="trips/:tripId" element={lazyRoute(<TripTimeline />)} />
        <Route path="lab" element={lazyRoute(<CompareLab />)} />
        <Route path="alerts" element={lazyRoute(<AlertsCenter />)} />
        <Route path="signin" element={<SignIn />} />
        <Route path="recover" element={<Recover />} />
        <Route path="auth/verify" element={lazyRoute(<AuthVerify />)} />
        {import.meta.env.DEV ? <Route path="__gallery" element={lazyRoute(<Gallery />)} /> : null}
      </Route>
      <Route path="admin" element={<AdminShell />}>
        <Route path="login" element={lazyRoute(<AdminLogin />)} />
        <Route index element={<RequireAdmin>{lazyRoute(<AdminDashboard />)}</RequireAdmin>} />
        <Route path="usage" element={<RequireAdmin>{lazyRoute(<AdminUsage />)}</RequireAdmin>} />
        <Route path="data" element={<RequireAdmin>{lazyRoute(<DataSummary />)}</RequireAdmin>} />
        <Route path="system" element={<RequireAdmin>{lazyRoute(<AdminSystem />)}</RequireAdmin>} />
        <Route path="errors" element={<RequireAdmin>{lazyRoute(<AdminErrors />)}</RequireAdmin>} />
        <Route
          path="errors/:groupId"
          element={<RequireAdmin>{lazyRoute(<AdminErrorDetail />)}</RequireAdmin>}
        />
      </Route>
      {/* Design concept routes — admin-gated but rendered without AdminShell
          so variants feel like the real anonymous app under review. See
          docs/design-philosophy.md §4. */}
      <Route
        path="admin/design"
        element={<RequireAdmin>{lazyRoute(<DesignIndex />)}</RequireAdmin>}
      />
      <Route
        path="admin/design/today-trip-basis/overflow-menu"
        element={<RequireAdmin>{lazyRoute(<DesignTodayTripBasisOverflowMenu />)}</RequireAdmin>}
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
